import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { DEFAULT_TASKLIST_PREFERENCES } from "@/common/constants";
import { useTRPC } from "@/lib/trpc";
import { useSuspenseQueryDeferred } from "@/lib/useSuspenseQueryDeferred";
import { preparePreferenceTaskListAttributes } from "@/lib/utils";
import type { TaskListParams } from "@/types/list";

export const useTaskListsQueryOptions = () => {
    const trpc = useTRPC();
    const opts = trpc.taskList.list.queryOptions(undefined, {
        refetchOnWindowFocus: true,
    });
    return opts;
};

export const useTaskLists = ({ enabled = true }: { enabled?: boolean } = { enabled: true }) => {
    const trpc = useTRPC();
    const opts = trpc.taskList.list.queryOptions(undefined, {
        refetchOnWindowFocus: true,
        enabled: enabled,
        staleTime: 5000 * 60, // 5 minute
        gcTime: 5000 * 60,
    });
    const q = useQuery(opts);

    return q;
};

export const useSuspenseTaskLists = () => {
    const trpc = useTRPC();
    const opts = trpc.taskList.list.queryOptions(undefined, {
        refetchOnWindowFocus: true,
    });
    const q = useSuspenseQuery(opts);

    return q;
};

export const useTaskListQueryOptions = (id: number) => {
    const trpc = useTRPC();
    return trpc.taskList.get.queryOptions({ id });
};

export const useTaskList = (id: number) => {
    const opts = useTaskListQueryOptions(id);
    const q = useQuery(opts);

    return q;
};

export const useOptionalTaskList = (taskListId: number | undefined) => {
    // We're re-using the list query because we can heavily cache that anyway.
    const { data: taskLists } = useTaskLists();
    return taskLists?.find((list) => list.id === taskListId);
};

export const useSuspenseTaskList = (id: number) => {
    const opts = useTaskListQueryOptions(id);
    const q = useSuspenseQuery(opts);

    return q;
};

export const useSuspenseTaskListUsers = (id: number) => {
    const trpc = useTRPC();
    const opts = trpc.taskList.users.queryOptions({ id });
    const q = useSuspenseQuery(opts);

    return q;
};

export const useTaskListUsers = (id: number) => {
    const trpc = useTRPC();
    const opts = trpc.taskList.users.queryOptions({ id });
    return useQuery(opts);
};

const useTaskListPreferencesQueryOptions = ({ type, taskListId, parentTaskId }: TaskListParams) => {
    const trpc = useTRPC();
    return trpc.taskList.getPreferences.queryOptions(
        {
            taskListId,
            parentTaskId,
            type,
        },
        {
            staleTime: 10000, // we use the query cache as an easy way to not call the API too often in a single window load
        }
    );
};

export const useTaskListPreferences = (params: TaskListParams) => {
    const opts = useTaskListPreferencesQueryOptions(params);
    const { data } = useQuery(opts);

    const result = useMemo(() => {
        const result = { ...DEFAULT_TASKLIST_PREFERENCES, ...data };
        if (result["taskList.attributes"]) {
            result["taskList.attributes"] = preparePreferenceTaskListAttributes(result["taskList.attributes"]);
        }
        return { preferences: result };
    }, [data]);

    return result;
};

export const useSuspenseTaskListPreferences = (params: TaskListParams) => {
    const opts = useTaskListPreferencesQueryOptions(params);
    const { data } = useSuspenseQuery(opts);

    const result = useMemo(() => {
        const result = { ...DEFAULT_TASKLIST_PREFERENCES, ...data };
        if (result["taskList.attributes"]) {
            result["taskList.attributes"] = preparePreferenceTaskListAttributes(result["taskList.attributes"]);
        }
        return { preferences: result };
    }, [data]);

    return result;
};

export const useSuspenseDeferredTaskListPreferences = (params: TaskListParams) => {
    const opts = useTaskListPreferencesQueryOptions(params);
    const { data } = useSuspenseQueryDeferred(opts);

    return useMemo(() => {
        const result = { ...DEFAULT_TASKLIST_PREFERENCES, ...data };
        if (result["taskList.attributes"]) {
            result["taskList.attributes"] = preparePreferenceTaskListAttributes(result["taskList.attributes"]);
        }
        return { preferences: result };
    }, [data]);
};
